import React, { useCallback, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import PagePanel from "app/components/PagePanel";
import Button from "app/components/Button/ButtonVariant";
import { useActions, useSelector } from "app/utils/hooks";
import {
  fetchMediaById,
  deleteMedia,
  editMedia,
} from "app/actions/media-library-data";
import { RootState } from "typedefs";
import { cls } from "app/utils";
import { TagSelect } from "app/components/inputs/SelectBox";
import TagModel from "app/models/TagModel";
import { shallowEqual } from "react-redux";
import SegmentsLayout from "app/components/Layouts/SegmentsLayout";
import { useTranslation } from "react-i18next";
import { useHistory, Link, useParams } from "react-router-dom";
import PostMedia from "app/components/Post/InnerPost/PostMedia";

const EditResource = () => {
  const { t } = useTranslation();

  const { id } = useParams<{ id: string }>();
  const resourceActions = useActions({
    fetchMediaById,
    deleteMedia,
    editMedia,
  });
  const history = useHistory();

  const [attachment, setAttachment] = useState(null);

  const route = (path: string) => {
    history.push(path);
  };
  const mediaData = useSelector(
    (state: RootState) => state.mediaLibraryData,
    shallowEqual,
  );

  const [tags, selectedTags] = useState<string[]>([]);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const response = await resourceActions.fetchMediaById(id);
        if (response) {
          setAttachment(response);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchMedia();
  }, []);

  const onTagsChange = useCallback(
    (tags) => {
      const tagNames = tags.map((tag: TagModel) => tag.name);

      selectedTags(tagNames);
    },
    [tags],
  );

  const onSaveChanges = () => {
    if (mediaData) {
      resourceActions
        .editMedia(attachment.id, {
          ...attachment,
          tags,
        })
        .then(() => (window.location.pathname = "/resources"));
    }
  };

  const handleDeleteMedia = useCallback(() => {
    if (window.confirm(t("Are you sure you want to delete this media file?"))) {
      resourceActions
        .deleteMedia(id)
        .finally(() => route("/resources"));
    } else {
      return null;
    }
  }, []);

  const getButtonTitle = () => {
    if (window.innerWidth <= 768) {
      return t("Create post");
    } else {
      return t("Create post with media");
    }
  };

  const getDeleteButtonTitle = () => {
    if (window.innerWidth <= 768) {
      return "";
    } else {
      return t("Delete");
    }
  };

  const headerComponent = () => {
    return (
      <span className={styles.header_buttons}>
        <Link to={`/resources/${id}/post`}>
          <Button
            icon={<i className="icon-plus icon-white" />}
            childStyles={cls(styles.btn, styles.create_post_btn)}
            title={getButtonTitle()}
            buttonType="primary"
          />
        </Link>
        <Button
          title={getDeleteButtonTitle()}
          icon={<i className="icon-trash icon-white" />}
          childStyles={styles.btn}
          buttonType="danger"
          onClickFunc={handleDeleteMedia}
        />
      </span>
    );
  };

  return (
    <SegmentsLayout
      isLoading={mediaData.pending || !attachment}
      displayNoResult={!id}
    >
      <div className={styles.container}>
        <PagePanel title={t("Edit Media")} headerChildren={headerComponent()} />

        <div className={styles.content_wrapper}>
          <PostMedia mediaData={attachment} />
          <TagSelect defaultValue={attachment?.tags} onChange={onTagsChange} />
          <span className={styles.header_buttons}>
            <Link to="/resources">
              <Button
                title={t("Cancel")}
                buttonType="secondary-outline"
                buttonStyles={styles.control_btn}
              />
            </Link>
            <Button
              title={t("Save changes")}
              buttonType="primary"
              onClickFunc={onSaveChanges}
            />
          </span>
        </div>
      </div>
    </SegmentsLayout>
  );
};

export default EditResource;
